import React, { useEffect, useState } from "react";
import api from "../../api";
import { Loader2, Copy, Gift, TrendingDown, Users } from "lucide-react";
import { toast } from "../../hooks/use-toast";

export default function ReferralsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/business/referrals").then(({ data }) => setData(data)); }, []);
  if (!data) return <Loader2 className="animate-spin text-[#2ec9c9]" size={32} />;

  const link = `${window.location.origin}${data.signup_path}`;
  const copy = () => { navigator.clipboard?.writeText(link); toast({ title: "Referral link copied" }); };
  const saved = Math.round((data.base_commission_pct - data.effective_commission_pct) * 100) / 100;
  const maxRefs = data.discount_per_referral > 0 ? Math.ceil(data.max_discount / data.discount_per_referral) : 0;

  return (
    <div className="space-y-6" data-testid="referrals-tab">
      <div className="rounded-2xl bg-[#16243b] text-white p-7">
        <div className="flex items-center gap-2 text-[#2ec9c9] text-sm font-medium"><Gift size={16} /> Refer a business, pay less</div>
        <h2 className="text-2xl font-bold mt-2">Cut your commission by {data.discount_per_referral}% for every business you refer</h2>
        <p className="text-white/70 text-sm mt-2">Up to {data.max_discount}% off ({maxRefs} referrals). Discount applies automatically to every future sale while they stay active.</p>
        <div className="mt-5 flex flex-col sm:flex-row gap-3">
          <input readOnly value={link} data-testid="referral-link" className="flex-1 rounded-xl bg-white/10 border border-white/15 px-4 py-2.5 text-sm font-mono outline-none" />
          <button onClick={copy} data-testid="copy-referral-link" className="gp-btn-teal rounded-xl px-5 py-2.5 text-sm font-medium inline-flex items-center justify-center gap-2"><Copy size={15} /> Copy link</button>
        </div>
        <p className="text-xs text-white/50 mt-2">Your code: <span className="font-mono text-white/80" data-testid="referral-code">{data.code}</span></p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <Stat icon={Users} label="Businesses referred" value={data.count} testid="referral-count" />
        <Stat icon={TrendingDown} label="Your commission rate" value={`${data.effective_commission_pct}%`} testid="referral-effective-pct" />
        <Stat icon={Gift} label="Discount earned" value={`${saved}%`} testid="referral-saved" />
      </div>

      <div className="bg-white rounded-2xl shadow-[0_8px_24px_rgba(20,30,55,0.06)] overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-50 font-semibold text-[#16243b]">Your referrals</div>
        {data.referred.length === 0 ? (
          <p className="px-5 py-10 text-center text-[#8a97a8] text-sm" data-testid="referrals-empty">No referrals yet — share your link with other local businesses.</p>
        ) : (
          <ul>
            {data.referred.map((r, i) => (
              <li key={i} className="px-5 py-3 border-t border-gray-50 flex justify-between text-sm" data-testid={`referral-row-${i}`}>
                <span className="font-medium text-[#16243b]">{r.name}</span>
                <span className="text-[#8a97a8]">Joined {String(r.created_at).slice(0, 10)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
      <Icon className="text-[#2ec9c9]" size={20} />
      <p className="text-2xl font-bold text-[#16243b] mt-2" data-testid={testid}>{value}</p>
      <p className="text-sm text-[#8a97a8]">{label}</p>
    </div>
  );
}
